import { Icon } from '../Brand'
import { agentMeta } from '../UserWorkspace/agentMeta'

// Shared assistant-message rendering, factored out of Chat.jsx so both the admin
// Chat and the user-facing UserWorkspace render answers identically (DRY).

// Minimal, safe inline formatting: **bold** and line breaks. The message text
// comes from our own backend, but we still render via React nodes (no
// dangerouslySetInnerHTML) to avoid injecting markup.
function renderInline(text) {
  const lines = String(text).split('\n')
  return lines.map((line, i) => (
    <span key={i}>
      {line.split(/(\*\*[^*]+\*\*)/g).map((seg, j) =>
        seg.startsWith('**') && seg.endsWith('**')
          ? <strong key={j}>{seg.slice(2, -2)}</strong>
          : <span key={j}>{seg}</span>
      )}
      {i < lines.length - 1 && <br />}
    </span>
  ))
}

// "| a | b |" → ["a", "b"] (tolerates missing outer pipes).
function splitCells(line) {
  let s = line.trim()
  if (s.startsWith('|')) s = s.slice(1)
  if (s.endsWith('|')) s = s.slice(0, -1)
  return s.split('|').map((c) => c.trim())
}

// A GitHub-markdown table separator row, e.g. "| --- | :--: |".
const isTableSep = (line) =>
  line.includes('|') && line.includes('-') && /^[\s:|-]+$/.test(line)

// Render message text, upgrading GitHub-style markdown tables (a pipe header row
// followed by a `---` separator) into real tables via <TableArtifact>. Some
// models emit tabular data inline in the message instead of as a structured
// artifact; this makes both render the same. Non-table text keeps **bold** + breaks.
export function renderText(text) {
  const lines = String(text).split('\n')
  const out = []
  let buf = []
  const flushText = () => {
    if (buf.length) {
      out.push(<div key={`t${out.length}`}>{renderInline(buf.join('\n'))}</div>)
      buf = []
    }
  }
  for (let i = 0; i < lines.length; i++) {
    const isHeader = lines[i].includes('|') && lines[i + 1] != null && isTableSep(lines[i + 1])
    if (isHeader) {
      flushText()
      const columns = splitCells(lines[i])
      const rows = []
      i += 2 // skip the header + separator rows
      while (i < lines.length && lines[i].includes('|') && lines[i].trim()) {
        rows.push(splitCells(lines[i]))
        i += 1
      }
      i -= 1 // step back so the for-loop's ++ lands on the first non-table line
      out.push(<TableArtifact key={`tbl${out.length}`} data={{ columns, rows }} />)
    } else {
      buf.push(lines[i])
    }
  }
  flushText()
  return out
}

export function TableArtifact({ data }) {
  const columns = data?.columns || []
  const rows = data?.rows || []
  if (!columns.length && !rows.length) return null
  return (
    <div className="chat-table-wrap">
      <table className="chat-table">
        <thead>
          <tr>{columns.map((c, i) => <th key={i}>{String(c)}</th>)}</tr>
        </thead>
        <tbody>
          {rows.map((row, i) => (
            <tr key={i}>
              {(row || []).map((cell, j) => <td key={j}>{cell == null ? '' : String(cell)}</td>)}
            </tr>
          ))}
        </tbody>
      </table>
      {typeof data?.total_rows === 'number' && data.total_rows > rows.length && (
        <div className="chat-table-note">Showing {rows.length} of {data.total_rows} rows</div>
      )}
    </div>
  )
}

function Artifact({ artifact }) {
  if (artifact?.type === 'Table') return <TableArtifact data={artifact.data} />
  // Chart / Csv / Pdf rendering is out of scope for Design 1.
  return (
    <div className="chat-attachment">
      <Icon name="attachment" />
      <span>{artifact?.type || 'Attachment'}</span>
    </div>
  )
}

// A handlebars artifact token, e.g. `{{travel_budget_table}}`. The backend
// leaves these in the answer text to mark where a structured artifact belongs.
const ARTIFACT_TOKEN = /\{\{\s*([^{}]+?)\s*\}\}/g

// Render the answer body, substituting each `{{<id>}}` token in place with the
// artifact whose id matches (so the literal token is never shown). Text between
// tokens is rendered via the existing inline renderer (markdown tables/bold).
// Unmatched tokens are stripped; artifacts not referenced by any token are
// appended after the text so nothing is lost.
export function renderMessageBody(text, artifacts) {
  const list = Array.isArray(artifacts) ? artifacts : []
  const byId = new Map()
  for (const a of list) {
    if (a && a.id != null && !byId.has(String(a.id))) byId.set(String(a.id), a)
  }
  const used = new Set()
  const nodes = []
  const src = String(text || '')
  let last = 0
  let k = 0
  let m
  ARTIFACT_TOKEN.lastIndex = 0
  while ((m = ARTIFACT_TOKEN.exec(src)) !== null) {
    const before = src.slice(last, m.index)
    if (before) nodes.push(<div key={`s${k++}`} className="chat-text">{renderText(before)}</div>)
    const id = String(m[1]).trim()
    const art = byId.get(id)
    if (art) {
      nodes.push(<Artifact key={`a${k++}`} artifact={art} />)
      used.add(id)
    }
    last = m.index + m[0].length
  }
  const rest = src.slice(last)
  if (rest) nodes.push(<div key={`s${k++}`} className="chat-text">{renderText(rest)}</div>)
  list.forEach((a, i) => {
    if (a && a.id != null && used.has(String(a.id))) return
    nodes.push(<Artifact key={`u${i}`} artifact={a} />)
  })
  return nodes
}

// Map a source's raw `type` to plain-English chip text + a tooltip. Falls back
// gracefully for unknown types. The optional table/file detail is appended.
function sourceLabel(s) {
  const map = {
    data_plane: { text: 'Live data', title: 'Answered using a connected live data source' },
    knowledge_base: { text: 'Knowledge base', title: 'Answered from indexed documents' },
  }
  const base = map[s?.type] || { text: s?.type || 'source', title: '' }
  const detail = s?.table || s?.file || ''
  return { text: detail ? `${base.text} · ${detail}` : base.text, title: base.title }
}

export function AssistantMessage({ msg }) {
  const sources = msg.sources || []
  return (
    <div className="chat-msg assistant">
      <div className="chat-avatar assistant"><Icon name="auto_awesome" /></div>
      <div className="chat-bubble">
        {renderMessageBody(msg.text, msg.artifacts)}
        {sources.length > 0 && (
          <div className="chat-sources">
            {sources.map((s, i) => {
              const { text, title } = sourceLabel(s)
              return (
                <span key={i} className="chat-chip" title={title || undefined}>
                  <Icon name="database" />{text}
                </span>
              )
            })}
          </div>
        )}
        {(msg.routedTo || []).length > 0 && (
          <div className="chat-sources">
            {msg.routedTo.map((name) => (
              <span key={name} className="chat-chip">
                <Icon name="alt_route" />routed to {agentMeta(name).label}
              </span>
            ))}
          </div>
        )}
      </div>
    </div>
  )
}
